import { Button, BhiFormDialog, TextInput } from 'src/UIKit';
import { Box } from '@mui/material';
import React, { Fragment, useEffect, useState } from 'react';
import { useSelector } from 'react-redux';
import { useBlocklistApi } from 'src/modules/blocklist/api/BlocklistApi';
import { useCreateMessageTemplateApi, useUpdateMessageTemplateApi } from 'src/api/messageTemplates';
import BannedWordsModal from 'src/components/BannedWordsModal/view';
import { DefaultCustomField, MessageTemplate } from 'src/constants/types';
import { addMessageTemplateValidationSchema, ValidationErrors } from 'src/constants/validations';
import { convertHtmlToText } from 'src/helpers/helpers';
import { getCurrentCompanyId } from 'src/modules/companies/slices';
import { selectProfile } from 'src/modules/profile/slices';
import * as Yup from 'yup';
import TemplateText from 'src/components/TemplateText/TemplateText';

type CreateCompanyModalType = {
  show: boolean;
  setShowModal: React.Dispatch<React.SetStateAction<boolean>>;
  initialMessageTemplate: MessageTemplate;
  messageTemplate: MessageTemplate | undefined;
  metadata: DefaultCustomField[] | undefined;
  refreshMessageTemplates: () => void;
};

export default function CreateMessageTemplateModal({
  show,
  setShowModal,
  initialMessageTemplate,
  messageTemplate,
  refreshMessageTemplates,
  metadata,
}: CreateCompanyModalType) {
  const [errors, setErrors] = useState<ValidationErrors>({});
  const selectedCompanyId = useSelector(getCurrentCompanyId);
  const createMessageTemplate = useCreateMessageTemplateApi();
  const updateMessageTemplate = useUpdateMessageTemplateApi();
  const { profile } = useSelector(selectProfile);
  const validateText = useBlocklistApi().validate;
  const [formValues, setFormValues] = useState<MessageTemplate>(
    messageTemplate || initialMessageTemplate,
  );
  const [isTemplateReady, setIsTemplateReady] = useState<boolean>(false);

  useEffect(() => {
    setFormValues(messageTemplate?.id ? messageTemplate : initialMessageTemplate);
  }, [messageTemplate, initialMessageTemplate]);

  useEffect(() => {
    if (show && formValues && metadata) {
      // Small delay to ensure all data is properly loaded
      const timer = setTimeout(() => {
        setIsTemplateReady(true);
      }, 100);
      return () => clearTimeout(timer);
    } else {
      setIsTemplateReady(false);
    }
  }, [show, formValues, metadata]);

  const [showbannedWordsModal, setShowBannedWordsModal] = useState<boolean>(false);

  const handleClose = () => {
    setFormValues(initialMessageTemplate);
    setShowModal(false);
    setErrors({});
    setIsTemplateReady(false);
  };

  const createTemplate = () => {
    createMessageTemplate(formValues.name, formValues.text, selectedCompanyId!)
      .then(response => {
        setFormValues(response.data);
        handleClose();
        refreshMessageTemplates();
      })
      .catch(e => {
        if (e.error === 'conflict') {
          setErrors({
            name: 'Message template with this name already exists',
          });
        }
        if (e.error.message && e.error.message.includes('already exists')) {
          setErrors({
            name: 'Message template with this name already exists',
          });
        }
      });
  };

  const updateTemplate = () => {
    updateMessageTemplate(
      formValues.name,
      formValues.text,
      formValues.id!,
      selectedCompanyId!,
    ).then(() => {
      refreshMessageTemplates();
      handleClose();
    });
  };

  const handleMessageTemplateSave = (skipValidation: boolean = false) => {
    const isValid = validate(formValues);
    if (isValid && selectedCompanyId) {
      if (skipValidation) {
        if (formValues.id) {
          updateTemplate();
        } else {
          createTemplate();
        }
      } else {
        validateText({ texts: [formValues.text] })
          .then(() => {
            if (formValues.id) {
              updateTemplate();
            } else {
              createTemplate();
            }
          })
          .catch((error: { code: number }) => {
            if (error.code === 422) {
              setShowBannedWordsModal(true);
            }
            console.log(error);
          });
      }
    }
  };

  const handleChange = (e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>) => {
    const { name, value } = e.target;
    setFormValues({ ...formValues, [name]: value });
  };

  const isValidForm = (values: typeof formValues) => {
    try {
      addMessageTemplateValidationSchema.validateSync(values, {
        abortEarly: false,
      });
      return true;
    } catch {
      return false;
    }
  };

  const validate = (values: typeof formValues) => {
    try {
      addMessageTemplateValidationSchema.validateSync(values, {
        abortEarly: false,
      });
      setErrors({});
      return true;
    } catch (err) {
      if (err instanceof Yup.ValidationError) {
        const newErrors = err.inner?.reduce((acc, curr) => {
          if (curr.path) {
            acc[curr.path] = curr?.message;
          }
          return acc;
        }, {} as ValidationErrors);
        setErrors(newErrors);
      }
      return false;
    }
  };

  useEffect(() => {
    if (messageTemplate?.id) {
      setFormValues(messageTemplate);
    }
  }, [messageTemplate]);

  if (messageTemplate?.id && !formValues.id) {
    return null;
  }

  return (
    <Fragment>
      <BhiFormDialog
        onClose={handleClose}
        open={show}
        paperSx={{ overflow: 'visible', '& .MuiDialogContent-root': { overflow: 'visible' } }}
        title={`${profile?.type === 'user' ? '' : formValues.id ? 'Update ' : 'Create '}Message Template`}
        actions={profile?.role?.name === 'company_user' ? (
          <Button variant="contained" onClick={handleClose}>Close</Button>
        ) : (
          <>
            <Button variant="outlined" onClick={handleClose}>Cancel</Button>
            <Button
              variant="contained"
              disabled={!isValidForm(formValues)}
              onClick={() => handleMessageTemplateSave()}
            >
              {formValues.id ? 'Save' : 'Create'}
            </Button>
          </>
        )}
      >
        <TextInput
          label="Name"
          name="name"
          margin="none"
          value={formValues.name}
          onChange={handleChange}
          disabled={profile?.role?.name === 'company_user'}
          error={!!errors.name}
          helperText={errors.name}
        />
        <Box sx={{ position: 'relative' }}>
          {isTemplateReady && (
            <TemplateText
              showConfirmationText={false}
              confirmationText=""
              setFocus
              customFields={metadata!}
              messageTemplates={undefined}
              hideMessageTemplates
              text={formValues.text || ''}
              updateText={newText => setFormValues({ ...formValues, text: convertHtmlToText(newText) })}
            />
          )}
        </Box>
      </BhiFormDialog>
      <BannedWordsModal
        isOpen={showbannedWordsModal}
        onConfirm={() => {
          handleMessageTemplateSave(true);
          setShowBannedWordsModal(false);
        }}
        onCancel={() => setShowBannedWordsModal(false)}
      />
    </Fragment>
  );
}
